import React from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Reuse the same yardline positions from YardlineSlider
type YardlinePosition = {
  value: number;
  label: string;
};

const YARDLINE_POSITIONS: YardlinePosition[] = [
  { value: 0, label: 'Endzone' },
  ...Array.from({ length: 49 }, (_, i) => ({ value: -(i + 1), label: `${-(i + 1)}` })),
  { value: 50, label: '50' },
  ...Array.from({ length: 49 }, (_, i) => ({ value: 49 - i, label: `${49 - i}` }))
];

export const YardlineInput: React.FC<{
  value: number;
  onChange: (value: number) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  // Find the current position in our list of valid positions
  const currentPosition = YARDLINE_POSITIONS.find(pos => pos.value === value);
  const currentIndex = YARDLINE_POSITIONS.findIndex(pos => pos.value === value);

  // Keep track of what's in the input field
  const [inputValue, setInputValue] = React.useState<string>(value === 0 ? 'Endzone' : value.toString());

  // Update input value when prop value changes
  React.useEffect(() => {
    setInputValue(currentPosition?.label === 'Endzone' ? 'Endzone' : value.toString());
  }, [value, currentPosition]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = event.target.value;
    setInputValue(newInputValue);
    
    // Handle empty input or just a minus sign - don't update the actual value yet
    if (newInputValue === '' || newInputValue === '-') {
      return;
    }

    // Special case for "Endzone"
    if (newInputValue.toLowerCase() === 'endzone') {
      onChange(0);
      return;
    }

    // Parse the input value
    const newValue = parseInt(newInputValue, 10);
    if (!isNaN(newValue)) {
      // Find the closest valid yardline position
      const validPosition = YARDLINE_POSITIONS.find(pos => pos.value === newValue);
      if (validPosition) {
        onChange(validPosition.value);
      }
    }
  };

  // Handle blur to reset to valid value if needed
  const handleBlur = () => {
    setInputValue(currentPosition?.label === 'Endzone' ? 'Endzone' : value.toString());
  };

  const movePosition = (direction: 'up' | 'down') => {
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' 
      ? Math.min(currentIndex + 1, YARDLINE_POSITIONS.length - 1)
      : Math.max(currentIndex - 1, 0);
    
    onChange(YARDLINE_POSITIONS[newIndex].value);
  };

  // Get display value (handle special case for Endzone)
  const displayValue = currentPosition?.label === 'Endzone' ? 'Endzone' : value.toString();

  return (
    <Box sx={{ width: "100%", px: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography id={`${label}-input-label`} gutterBottom>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => movePosition('down')}
            sx={{ 
              p: 1.5,
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'white'
              }
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
          <TextField
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            inputProps={{
              style: { 
                textAlign: "center",
              },
            }}
            sx={{ 
              width: 100,
              '& input': {
                p: 1,
                fontSize: '1.1rem',
                fontWeight: 'medium',
                // Hide spinner buttons for number input
                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '&[type=number]': {
                  MozAppearance: 'textfield',
                },
              }
            }}
          />
          <IconButton 
            onClick={() => movePosition('up')}
            sx={{ 
              p: 1.5,
              color: 'success.main',
              '&:hover': {
                backgroundColor: 'success.light',
                color: 'white'
              }
            }}
          >
            <KeyboardArrowUpIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
